<template>
  <div class="markdown-content" ref="markdownContainer">
    <!-- New chunked messages -->
    <template v-if="message.chunks?.length">
      <template v-for="(chunk, index) in message.chunks" :key="index">
        <div
          v-if="chunk.type === CHUNK_TYPE.TEXT"
          class="markdown-content"
          ref="textChunks"
          v-html="sanitizeAndParse(chunk.content)"
        />

        <ThinkingCard v-else-if="chunk.type === CHUNK_TYPE.RESEARCH" :title="chunk.data.key">
          <div class="markdown-content" ref="textChunks" v-html="sanitizeAndParse(chunk.data.value)" />
        </ThinkingCard>
      </template>
    </template>

    <!-- Legacy messages -->
    <div v-else class="markdown-content" v-html="renderedMarkdown"></div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, h, render, inject, getCurrentInstance } from 'vue';
import { marked } from 'marked';

import { CHUNK_TYPE } from '~/constants/conversationModels';

import useDocuments from '~/composables/useDocuments';
import { ensureLinksOpenInNewTab } from '~/utils/document-utils';
import { useSanitize } from '~/composables/useSanitize';

import ThinkingCard from '~/components/conversation/chatMessage/ThinkingCard';
import InfoTooltip from '~/components/conversation/chatMessage/InfoTooltip';
import {
  buildCitationDictFromPlaceholders as buildCitationDict,
  getCitationSourceData,
  isUnsubstantiatedCitation,
} from '~/utils/citations-utils';

export default {
  name: 'TextMessageWithTooltip',
  props: {
    message: {
      type: Object,
      default: () => ({}),
    },
  },
  components: { ThinkingCard },
  setup(props) {
    const currentInstance = getCurrentInstance();
    const arcaneApiGateway = inject('arcaneApiGateway');
    const { openDocumentFromTitle, getDocumentThumbs } = useDocuments();
    const { sanitize } = useSanitize();

    const markdownContainer = ref(null);
    const textChunks = ref([]);

    // Legacy message HTML
    const renderedMarkdown = computed(() => sanitizeAndParse(props.message.message));

    // Function to sanitize and parse text chunks; ensure links open in new tab
    const sanitizeAndParse = (content) => {
      const html = content?.replace(
        /\[([^\]]+?)-(\d+),\s*p\.(\d+)\]/g,
        (match, id, snippetIndex, page) =>
          `<span class="citation-placeholder" data-id="${id}" data-index="${snippetIndex}" data-page="${page}"></span>`,
      );

      const toParse = html !== undefined ? html : '';
      const parsed = props.message.isTyping
        ? marked.parse(toParse + '<span v-if="isTyping" class="cursor" />')
        : marked.parse(toParse);

      // Sanitize first, then ensure links open in a new tab
      const sanitized = sanitize(parsed);
      return ensureLinksOpenInNewTab(sanitized);
    };

    const handleTooltipTitle = async (payload, id) => {
      await openDocumentFromTitle(payload, id);
    };

    const loadTooltipThumb = async (id, page) => {
      const thumb = await getDocumentThumbs({
        service: arcaneApiGateway,
        documents: [{ id: id, pages: [page] }],
      });

      return thumb[0]?.pages[page]?.small || '';
    };

    const isCitationUnsubstantiated = (id, snippetIndex, page) => {
      const messageBlock = props.message?.data?.[0] || {};
      const references = Array.isArray(messageBlock.references) ? messageBlock.references : [];

      return isUnsubstantiatedCitation({ references, id, snippetIndex, page });
    };

    const updateTooltipsInElement = (el) => {
      if (!el) return;

      const placeholders = el.querySelectorAll('.citation-placeholder');
      const allReferences = (props.message.data || []).flatMap((block) => block.references || []);
      const dict = buildCitationDict(placeholders, allReferences);

      placeholders.forEach((ph) => {
        const id = ph.getAttribute('data-id');
        const snippetIndex = ph.getAttribute('data-index');
        const page = ph.getAttribute('data-page');

        const snippetKey = `${page}-${snippetIndex}`;
        const secondOrderRefs = allReferences
          .filter((ref) => ref.externalId === id)
          .flatMap((ref) => ref.second_order_references_by_snippet?.[snippetKey] || []);

        const phRect = ph.getBoundingClientRect();
        const containerRect = markdownContainer.value.getBoundingClientRect();

        const tooltipWidth = 300;
        const padding = 8;

        let tooltipLeftAbs = phRect.left - containerRect.left + phRect.width / 2 - tooltipWidth / 2;
        tooltipLeftAbs = Math.max(padding, Math.min(tooltipLeftAbs, containerRect.width - tooltipWidth - padding));
        let tooltipLeft = tooltipLeftAbs - (phRect.left - containerRect.left);

        // Ensure the tooltip is fully visible within the containerRect
        if (tooltipLeftAbs < padding) {
          tooltipLeft = padding - (phRect.left - containerRect.left);
        } else if (tooltipLeftAbs + tooltipWidth > containerRect.width - padding) {
          tooltipLeft = containerRect.width - tooltipWidth - padding - (phRect.left - containerRect.left);
        }

        const tooltipStyle = {
          left: `${tooltipLeft}px`,
        };

        const sourceData = {
          ...(getCitationSourceData(id) || {}),
          ...(isCitationUnsubstantiated(id, snippetIndex, page) && {
            unsubstantiated: true,
          }),
        };

        if (secondOrderRefs.length > 0) {
          const vnodes = secondOrderRefs.map(({ refNum, referenceId }) => {
            const vnode = h(InfoTooltip, {
              id,
              page,
              snippetIndex,
              refNum,
              referenceId,
              tooltipStyle,
              messageData: props.message,
              label: dict[`${id}-${referenceId}`],
              source: sourceData,
              onTitleClicked: (payload) => handleTooltipTitle(payload, id),
              onGetTooltipThumb: loadTooltipThumb,
            });
            // Preserve app-level provides (vue-i18n, store, etc.) for programmatically mounted tooltips.
            vnode.appContext = currentInstance?.appContext;
            return vnode;
          });
          render(h('span', { style: 'display: contents' }, vnodes), ph);
        } else {
          const vnode = h(InfoTooltip, {
            id,
            page,
            snippetIndex,
            tooltipStyle,
            messageData: props.message,
            label: dict[`${id}-${snippetIndex}`],
            source: sourceData,
            onTitleClicked: (payload) => handleTooltipTitle(payload, id),
            onGetTooltipThumb: loadTooltipThumb,
          });

          vnode.appContext = currentInstance?.appContext;

          render(vnode, ph);
        }
      });
    };

    const updateAllTooltips = () => {
      // Legacy messages
      if (!props.message.chunks?.length && markdownContainer.value) {
        updateTooltipsInElement(markdownContainer.value);
      }

      // Chunked text messages
      if (props.message.chunks?.length) {
        textChunks.value.forEach((el) => {
          updateTooltipsInElement(el);
        });
      }
    };

    onMounted(() => {
      nextTick(() => {
        updateAllTooltips();
      });
    });

    watch(
      () => [props.message?.data?.length, props.message.chunks],
      () => {
        nextTick(() => {
          updateAllTooltips();
        });
      },
      { deep: true },
    );

    return {
      markdownContainer,
      textChunks,
      renderedMarkdown,
      sanitizeAndParse,
      CHUNK_TYPE,
    };
  },
};
</script>

<style scoped lang="scss">
.markdown-content {
  word-break: break-word;
  font-size: 15px;
  line-height: $bb-spacing-5;
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-4;

  :deep(a) {
    color: $bb-primary-3;
  }

  :deep(ul),
  :deep(ol) {
    padding-left: $bb-spacing-6;
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-3;

    //&:not(:last-child) {
    // padding-bottom: $bb-spacing-3;
    //}
  }

  :deep(table) {
    border-collapse: collapse;
    outline: 1px solid #ccc;
    outline-offset: -1px;
    border-radius: 16px;
  }

  :deep(th) {
    background-color: $bb-neutral-6;
  }

  :deep(th),
  :deep(td) {
    vertical-align: middle;
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
    padding: $bb-spacing-2 $bb-spacing-4;
    border-right: 1px solid $bb-neutral-4;
    border-bottom: 1px solid $bb-neutral-4;
  }

  :deep(tr:last-child td) {
    border-bottom: 0;
  }

  :deep(th:last-child),
  :deep(td:last-child) {
    border-right: 0;
  }

  :deep(.cursor) {
    display: inline-block;
    margin-left: $bb-spacing-1;
    width: 2px;
    height: 1em;
    background-color: $bb-primary-3;
    animation: blink-animation 1s steps(20, start) infinite;
    @keyframes blink-animation {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0;
      }
    }
  }
}
</style>
